/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { StatusRenderer } from "./error-screen";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNetworkError } from "../utils/useNetworkError";
import {
  formatMoney,
  handleRetrieveCartOrders,
  handleRetrieveRetailOutlet,
  handleRetrieveUser,
  printErrorMessage,
  refreshToOrderSummaryPage,
} from "../utils/functions";

const CheckoutForm = ({ clientSecret, setStripeHasError, setErrorMessage }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(true);

  useEffect(() => {
    if (elements) {
      const element = elements.getElement("payment");
      element.on("ready", () => {
        setIsStripeLoading(false);
      });
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (elements == null) {
      return;
    }
    setIsProcessing(true);

    // elements.

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();

    if (submitError) {
      // Show error to your customer
      setIsProcessing(false);
      setErrorMessage(submitError.message);
      return;
    }

    // Create the PaymentIntent and obtain clientSecret from your server endpoint

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/wholesale/confirm-payment`,
      },
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setStripeHasError(true);
      setIsProcessing(false);
      setErrorMessage(error.message);
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  return (
    <form id='payment-form' onSubmit={handleSubmit}>
      <PaymentElement />
      <button type='submit' disabled={isStripeLoading || isProcessing}>
        {isProcessing ? (
          <div class='spinner-border text-success app-spinner' role='status'>
            <span class='sr-only'>Loading...</span>
          </div>
        ) : (
          "Process Payment"
        )}
      </button>
    </form>
  );
};

export function Payment() {
  const {
    state: {
      country = "",
      cartOrder,
      orderPaymentReference = "",
      amount,
      creditBalance = 0,
      walletCapacity = 0,
    },
  } = useLocation();

  return country === "NG" ? (
    <PayWithBankTransfer
      amount={amount}
      creditBalance={creditBalance}
      walletCapacity={walletCapacity}
      orderPaymentReference={orderPaymentReference}
    />
  ) : (
    <PayWithStripe cartOrder={cartOrder} />
  );
}

function PayWithBankTransfer({
  amount: balance,
  creditBalance,
  walletCapacity,
  orderPaymentReference,
}) {
  const navigate = useNavigate();
  const user = handleRetrieveUser();
  const { authToken } = handleRetrieveCartOrders();
  const { account } = handleRetrieveRetailOutlet();

  useEffect(() => {
    document.getElementById("trigger-transfer-button").click();
  }, []);

  const initialMinutes = 15;
  const [minutes, setMinutes] = useState(initialMinutes);
  const [seconds, setSeconds] = useState(0);
  const [moneySent, setMoneySent] = useState(false);
  const [canBalancePay, setCanBalancePay] = useState(false);
  const [hasTimerExpired, setTimerExpired] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const copiedTimeoutRef = useRef(null);
  const pollFailureCountRef = useRef(0);
  const endTimeRef = useRef(Date.now() + initialMinutes * 60 * 1000);
  const [error, setError] = useState({
    hasError: false,
    message: "",
    title: "",
  });
  useNetworkError(setError);

  const handleCopy = (fieldName) => {
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    setCopiedField(fieldName);
    copiedTimeoutRef.current = setTimeout(() => setCopiedField(null), 2000);
  };

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  const handleCloseModalAndRedirect = () => {
    document.getElementById("btn-payment-modal-close").click();
    let url = `/wholesale/checkout?token=${authToken}`;
    if (orderPaymentReference)
      url = url + `&orderPaymentReference=${orderPaymentReference}`;
    return window.location.replace(url);
  };

  const handleCloseModal = () =>
    document.getElementById("btn-payment-modal-soft-close").click();

  useEffect(() => {
    // sets timer
    const intervalId = setInterval(() => {
      const remainingMs = Math.max(0, endTimeRef.current - Date.now());
      if (remainingMs <= 0) {
        setMinutes(0);
        setSeconds(0);
        setTimerExpired(true);
        clearInterval(intervalId);
        return;
      }
      const totalSeconds = Math.ceil(remainingMs / 1000);
      setMinutes(Math.floor(totalSeconds / 60));
      setSeconds(totalSeconds % 60);
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const timerExpired = minutes === 0 && seconds === 0;

  useEffect(() => {
    if (canBalancePay || error.hasError) return;

    let intervalMs;
    if (moneySent) {
      intervalMs = 5000;
    } else if (timerExpired) {
      intervalMs = 10000;
    } else {
      intervalMs = 15000;
    }
    handleFetchBalances();
    const intervalId = setInterval(handleFetchBalances, intervalMs);
    return () => clearInterval(intervalId);
  }, [canBalancePay, moneySent, timerExpired, error.hasError]);

  const plainBalanceUrl = `${process.env.REACT_APP_CONSOLE_V3}/retail/balance`;
  const balanceUrl = orderPaymentReference
    ? `${plainBalanceUrl}?paymentReference=${orderPaymentReference}`
    : plainBalanceUrl;

  const handleFetchBalances = async () => {
    try {
      let account, transaction;
      try {
        const res = await axios.get(balanceUrl, {
          headers: { "x-api-key": user.apiKey },
        });
        ({ account, paymentTransaction: transaction } = res.data?.data || {});
      } catch (refErr) {
        const res = await axios.get(plainBalanceUrl, {
          headers: { "x-api-key": user.apiKey },
        });
        ({ account } = res.data?.data || {});
      }
      pollFailureCountRef.current = 0;
      if (transaction?.status === "paid") {
        setCanBalancePay(true);
        handleCloseModal();
        return navigate("/wholesale/confirm-payment", {
          state: {
            reference: orderPaymentReference,
            hasOrderReference: true,
          },
        });
      }
      const hasSufficientBalance =
        account?.currentBalance >= walletCapacity + balance;
      const hasReceivedPayment =
        transaction?.amountPaid != null && transaction.amountPaid >= balance;
      if (hasSufficientBalance || hasReceivedPayment) {
        const chargeAmount = hasSufficientBalance
          ? walletCapacity + balance
          : account?.currentBalance || 0;
        setCanBalancePay(true);
        await handleChargeOrder(chargeAmount);
      } else if (timerExpired && !moneySent) handleCloseModalAndRedirect();
    } catch (error) {
      pollFailureCountRef.current += 1;
      if (timerExpired && !moneySent) {
        handleCloseModalAndRedirect();
        return;
      }
      handleCloseModal();
      if (pollFailureCountRef.current < 3) return;
      const { message, title } = printErrorMessage(error);
      setError({
        hasError: true,
        message,
        title,
        canRetry: true,
        retryText: "Try Again",
        retryFunction: () => {
          pollFailureCountRef.current = 0;
          // setError({ hasError: false, message: "", title: "" });
          refreshToOrderSummaryPage();
        },
      });
    }
  };

  const handleChargeOrder = async (walletMoney) => {
    handleCloseModal();
    console.log("[ChargeOrder] attempting charge-order:", {
      availableBalance: walletMoney,
      creditBalance,
      orderPaymentReference,
    });
    try {
      const body = {
        availableBalance: walletMoney,
        creditBalance,
      };
      if (orderPaymentReference)
        body["orderPaymentReference"] = orderPaymentReference;
      const {
        data: { data },
      } = await axios.post(
        `${process.env.REACT_APP_AWS_API_URL}/charge-order`,
        body,
        {
          headers: {
            "x-api-key": user?.apiKey,
            "Content-Type": "application/json",
          },
        },
      );
      // document.getElementById("btn-payment-modal-soft-close").click(); // close payment modal
      console.log(
        "[ChargeOrder] success → navigating with ref:",
        data.transactionReference,
      );
      return navigate("/wholesale/confirm-payment", {
        state: {
          reference: data.transactionReference,
          hasOrderReference: true,
        },
      });
    } catch (error) {
      console.error(
        "[ChargeOrder] charge-order failed:",
        error?.message,
        error?.response?.status,
        error?.response?.data,
      );
      try {
        let account, transaction;
        try {
          const res = await axios.get(balanceUrl, {
            headers: { "x-api-key": user.apiKey },
          });
          ({ account, transaction } = res.data?.data || {});
        } catch (_) {
          const res = await axios.get(plainBalanceUrl, {
            headers: { "x-api-key": user.apiKey },
          });
          ({ account } = res.data?.data || {});
        }
        console.log("[ChargeOrder] verify-after-fail:", {
          transactionStatus: transaction?.status,
          currentBalance: account?.currentBalance,
          walletMoney,
        });
        if (
          transaction?.status === "paid" ||
          (account?.currentBalance != null &&
            account.currentBalance < walletMoney)
        ) {
          console.log(
            "[ChargeOrder] verification indicates payment went through → navigating to success",
          );
          return navigate("/wholesale/confirm-payment", {
            state: {
              reference: orderPaymentReference,
              hasOrderReference: true,
            },
          });
        }
        console.log(
          "[ChargeOrder] verification shows payment NOT completed → showing error",
        );
      } catch (verifyErr) {
        console.error(
          "[ChargeOrder] verification call also failed:",
          verifyErr?.message,
        );
      }
      const { message, title } = printErrorMessage(error);
      handleCloseModal();
      setError({
        hasError: true,
        message,
        title,
        canRetry: true,
        retryText: "Go back to order",
        retryFunction: () => {
          console.log("retry function called");
          handleCloseModalAndRedirect();
        },
      });
    }
  };

  const handleSentMoney = () => {
    handleCloseModal();
    setMoneySent(true);
  };

  return error.hasError ? (
    <>
      <StatusRenderer
        message={error.message}
        title={error.title}
        canRetry={error?.canRetry || false}
        retryText={error?.retryText || null}
        retryFunction={error?.retryFunction || null}
      />
    </>
  ) : (
    <div className='trade-checkout_bank-transfer'>
      <button
        className='trigger-button'
        id='trigger-transfer-button'
        data-bs-toggle='modal'
        data-bs-target='#modalBankTransfer'
        style={{ display: "none" }}
      >
        Trigger Modal
      </button>
      <div
        className='trade-checkout-modal modal fade'
        id='modalBankTransfer'
        tabIndex='-1'
        aria-labelledby='modalBankTransferLabel'
        aria-hidden='true'
      >
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div class='modal-header'>
              <h1 class='modal-title fs-5' id='exampleModalLabel'>
                Payment
              </h1>
              <button
                type='button'
                class='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'
                id='btn-payment-modal-soft-close'
                onClick={() => handleCloseModal()}
                style={{ display: "none" }}
              >
                <i class='bi bi-x'></i>
              </button>
              <button
                type='button'
                class='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'
                id='btn-payment-modal-close'
                onClick={() => handleCloseModalAndRedirect()}
              >
                <i class='bi bi-x'></i>
              </button>
            </div>
            <div className='modal-body'>
              <p className='_transfer-text'>
                Transfer <b>NGN {formatMoney(balance)}</b> to the account below
              </p>
              <div className='_amount-to-pay'>
                <p>Amount to pay</p>
                <div>₦{formatMoney(balance)}</div>
              </div>
              <div className='_account-details'>
                <div>
                  <p>
                    <h6>bank name</h6>
                    <h5>{account?.secBankName}</h5>
                  </p>
                  <CopyToClipboard
                    text={account?.secBankName}
                    onCopy={() => handleCopy("bank")}
                  >
                    <span className='copy-btn' data-testid='copy-bank'>
                      {copiedField === "bank" ? (
                        <span className='copied-feedback'>
                          <i className='bi bi-check-lg'></i> Copied
                        </span>
                      ) : (
                        <i className='bi bi-copy'></i>
                      )}
                    </span>
                  </CopyToClipboard>
                </div>
                <div>
                  <p>
                    <h6>account number</h6>
                    <h5>
                      {account?.secAccountNumber ?? account?.accountNumber}
                    </h5>
                  </p>
                  <CopyToClipboard
                    text={account?.secAccountNumber ?? account?.accountNumber}
                    onCopy={() => handleCopy("account")}
                  >
                    <span className='copy-btn' data-testid='copy-account'>
                      {copiedField === "account" ? (
                        <span className='copied-feedback'>
                          <i className='bi bi-check-lg'></i> Copied
                        </span>
                      ) : (
                        <i className='bi bi-copy'></i>
                      )}
                    </span>
                  </CopyToClipboard>
                </div>
                <div>
                  <p>
                    <h6>amount</h6>
                    <h5>NGN {formatMoney(balance)}</h5>
                  </p>
                  <CopyToClipboard
                    text={balance}
                    onCopy={() => handleCopy("amount")}
                  >
                    <span className='copy-btn' data-testid='copy-amount'>
                      {copiedField === "amount" ? (
                        <span className='copied-feedback'>
                          <i className='bi bi-check-lg'></i> Copied
                        </span>
                      ) : (
                        <i className='bi bi-copy'></i>
                      )}
                    </span>
                  </CopyToClipboard>
                </div>
              </div>
              <center>
                <h6 className='_expires-text'>
                  Expires in{" "}
                  <span>
                    {minutes} minutes {seconds} seconds
                  </span>
                </h6>
              </center>
              <div className='_action-buttons'>
                <button className='_sent-money' onClick={handleSentMoney}>
                  I've sent the money
                </button>
                <button
                  className='_cancel-payment'
                  onClick={() => handleCloseModalAndRedirect()}
                >
                  Cancel Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {moneySent && (
        <div className='trade-checkout_confirm-payment'>
          <p className='repeat-container'>
            <i className='bi bi-arrow-repeat'></i>
          </p>
          <h3>Payment Confirmation</h3>
          {!hasTimerExpired ? (
            <>
              <h5>
                We are confirming your payment. This may take a few minutes.
              </h5>
              <div class='loader-line'></div>
              <div className='timer-expired-action'>
                <button
                  type='button'
                  className='trade-uk-app-button outline'
                  onClick={() => {
                    setMoneySent(false);
                    document.getElementById("trigger-transfer-button").click();
                  }}
                >
                  Go back
                </button>
              </div>
            </>
          ) : (
            <>
              <h5>
                It’s taking a while to confirm your payment, If you need any
                assistance, please don't hesitate to contact our support team.
              </h5>
              <div className='timer-expired-action'>
                <button
                  onClick={handleCloseModalAndRedirect}
                  className='trade-uk-app-button'
                >
                  Go to cart
                </button>
                {/* <button
                  className='intercom-custom-btn'
                  onClick={() => window.Intercom("show")}
                >
                  <img src='../images/intercom.svg' alt='' />
                  Contact Support
                </button> */}
              </div>
            </>
          )}
        </div>
      )}
      {/* error logs */}
    </div>
  );
}

function PayWithStripe({ cartOrder }) {
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeHasError, setStripeHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSetStripeData = useCallback(() => {
    setClientSecret(cartOrder.clientSecret);
    setStripePromise(loadStripe(cartOrder.publishableKey));
  }, []);

  useEffect(() => {
    if (cartOrder) handleSetStripeData();
  }, []);

  return stripeHasError ? (
    <StatusRenderer
      title={"Stripe Payment Failure"}
      message={errorMessage}
      canRetry={true}
      retryFunction={() => {
        setStripeHasError(false);
      }}
    />
  ) : (
    <div className='trade-uk_checkout-pay'>
      {stripePromise && clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, loader: "always" }}
        >
          <CheckoutForm
            clientSecret={clientSecret}
            setStripeHasError={setStripeHasError}
            setErrorMessage={setErrorMessage}
          />
        </Elements>
      )}
    </div>
  );
}
